import type { ReactNode } from "react";
import { initials } from "@digisubs/shared";
import { cn } from "@/lib/cn";

const GRADIENTS = [
  "from-[#2f6bff] to-[#5b8cff]",
  "from-[#7c3aed] to-[#a78bfa]",
  "from-[#0ea5e9] to-[#22d3ee]",
  "from-[#db2777] to-[#f472b6]",
  "from-[#059669] to-[#34d399]",
  "from-[#d97706] to-[#fbbf24]",
  "from-[#4f46e5] to-[#818cf8]",
];

function hash(value: string): number {
  let h = 0;
  for (let i = 0; i < value.length; i += 1) h = (h * 31 + value.charCodeAt(i)) | 0;
  return Math.abs(h);
}

const sizes = { xs: "size-6 text-[10px]", compact: "size-7 text-[10.5px]", sm: "size-8 text-[11px]", md: "size-10 text-[13px]", lg: "size-12 text-sm", xl: "size-16 text-lg" };

export function Avatar({
  name,
  src,
  size = "md",
  className,
  badge,
  square,
  gradient,
}: {
  name: string | null | undefined;
  src?: string | null;
  size?: keyof typeof sizes;
  className?: string;
  /** Small overlay at the bottom right (channel icon). */
  badge?: ReactNode;
  square?: boolean;
  /** Fixed gradient (the signed-in user is always blue, like the screenshots) instead of the name hash. */
  gradient?: string;
}) {
  const label = name || "Client";
  return (
    <span className={cn("relative inline-flex shrink-0", className)}>
      {src ? (
        <img src={src} alt="" loading="lazy" className={cn("object-cover ring-1 ring-line-strong", sizes[size], square ? "rounded-lg" : "rounded-full")} />
      ) : (
        <span
          aria-hidden
          className={cn(
            "inline-flex items-center justify-center bg-gradient-to-br font-semibold text-white",
            gradient ?? GRADIENTS[hash(label) % GRADIENTS.length],
            sizes[size],
            square ? "rounded-lg" : "rounded-full",
          )}
        >
          {initials(label)}
        </span>
      )}
      {badge ? <span className="absolute -bottom-0.5 -right-0.5 flex rounded-full ring-2 ring-panel">{badge}</span> : null}
    </span>
  );
}

/** Product / creative thumbnail with a neutral fallback. */
export function Thumb({ src, alt = "", size = "md", className }: { src?: string | null; alt?: string; size?: "sm" | "md" | "lg"; className?: string }) {
  const dims = size === "sm" ? "size-8" : size === "lg" ? "size-14" : "size-10";
  return src ? (
    <img src={src} alt={alt} loading="lazy" className={cn("shrink-0 rounded-lg object-cover ring-1 ring-line-strong", dims, className)} />
  ) : (
    <span className={cn("inline-flex shrink-0 items-center justify-center rounded-lg bg-panel-3 text-[10px] font-semibold uppercase text-muted ring-1 ring-line-strong", dims, className)}>
      {alt ? initials(alt) : "IMG"}
    </span>
  );
}
